import React from 'react'
import { observer } from 'mobx-react-lite'

import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'

import { Icon } from '@mxenabled/mxui'

import {
  Drawer,
  useAppConfig,
  useBudgetsStore,
  useGlobalCopyStore,
  useScreenSize,
  useTransactionStore,
} from '../../../common'
import { type DetailedBudget } from '../../../core'
import { exportTransactionsToCSV } from '../../../core/utils/TransactionUtils'

import { TransactionDetails, TransactionList } from '../../../transactions'

import { Header } from './Header'
import { SubBudgetList } from './SubBudgetList'

interface BudgetDetailsProps {
  budget: DetailedBudget
}

export const BudgetDetails: React.FC<BudgetDetailsProps> = observer(({ budget }) => {
  const { is_mobile_webview } = useAppConfig()
  const { setAlert } = useBudgetsStore()
  const { budgets: copy, common: commonCopy, transactions: transactionCopy } = useGlobalCopyStore()
  const { isDesktop, isSmallTablet, isTablet } = useScreenSize()
  const {
    sortedTransactions: transactions,
    sortedTransactionsWithSplits,
    tags,
  } = useTransactionStore()

  const [selectedTab, setSelectedTab] = React.useState(0)
  const [transactionGuid, setTransactionGuid] = React.useState('')

  const transaction = React.useMemo(
    () => transactions.find((t) => t.guid === transactionGuid),
    [transactionGuid, transactions],
  )

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue)
  }

  const handleExportToCsv = () => {
    exportTransactionsToCSV(sortedTransactionsWithSplits, tags)
    setAlert({ msg: copy.alert_csv_downloaded, severity: 'success' })
  }

  const shouldShowExportButton =
    transactions.length > 0 && !is_mobile_webview && (isDesktop || (isTablet && !isSmallTablet))

  return (
    <Stack gap={24}>
      <Header budget={budget} />

      <Stack>
        <Tabs
          onChange={handleTabChange}
          sx={{ width: '100%' }}
          textColor="primary"
          value={selectedTab}
          variant="fullWidth"
        >
          <Tab label={copy.details_transactions_tab} />
          <Tab label={copy.details_subbudgets_tab} />
        </Tabs>
        {selectedTab === 0 && (
          <Stack>
            {shouldShowExportButton && (
              <Stack direction="row" justifyContent="right" mb={-24} mr={8} mt={8} zIndex={1}>
                <Button
                  onClick={handleExportToCsv}
                  startIcon={<Icon name="ios_share" />}
                  variant="text"
                >
                  {transactionCopy.export_csv_btn}
                </Button>
              </Stack>
            )}
            <TransactionList height="100%" onClick={setTransactionGuid} />
          </Stack>
        )}
        {selectedTab === 1 && <SubBudgetList budget={budget} />}

        <Drawer
          ariaLabelClose={commonCopy.close_aria}
          isOpen={Boolean(transaction)}
          onClose={() => setTransactionGuid('')}
          title={copy.details_transaction_drawer}
        >
          {transaction && <TransactionDetails transaction={transaction} />}
        </Drawer>
      </Stack>
    </Stack>
  )
})
